import type { EraId } from '../config/eras';
import { ERAS } from '../config/eras';
import { eraForDate, spacedCycleForDate } from '../track/era';
import { Rng } from '../track/prng';

/**
 * Every day's title poster is printed fresh, the way race posters were: a new
 * colour scheme, the car drawn from a new angle, a new motif behind it. All
 * from the date, so everyone sees the same poster, and nothing repeats from
 * one day to the next.
 */
export interface PosterPalette {
  name: string;
  /** The painted field. */
  field: string;
  /** "grand premio" and other big lettering. */
  title: string;
  /** Dark-on-field lettering: the circuit's name, labels, rules. */
  ink: string;
  /** Small body text on the field (the ink, wherever cream would be too faint on the field to read). */
  text: string;
  /** The race button, and its lettering. */
  accent: string;
  accentText: string;
  /** Behind the car: the rays and the thin stripe (a shade of the field), the sun and the broad stripe. */
  motif: string;
  sun: string;
  /** Car colours this field would swallow. */
  clashes: string[];
}

const CREAM = '#F3E6C8';
const BROWN = '#3B2A1E';
const RED = '#B8352B';
const OCHRE = '#EDC27A';

export const PALETTES: PosterPalette[] = [
  { name: 'monza', field: '#8E9A58', title: CREAM, ink: BROWN, text: BROWN, accent: RED, accentText: CREAM, motif: '#A3AD6C', sun: CREAM, clashes: ['olive'] },
  { name: 'rosso', field: RED, title: CREAM, ink: BROWN, text: CREAM, accent: BROWN, accentText: CREAM, motif: '#C9483C', sun: CREAM, clashes: ['red', 'terracotta'] },
  { name: 'giallo', field: '#E3A93E', title: RED, ink: BROWN, text: BROWN, accent: RED, accentText: CREAM, motif: '#EDBE5E', sun: '#F6E7C1', clashes: ['ochre'] },
  { name: 'azzurro', field: '#5B8BA3', title: CREAM, ink: '#1E2E3A', text: '#1E2E3A', accent: RED, accentText: CREAM, motif: '#6E9CB3', sun: CREAM, clashes: ['silver'] },
  { name: 'notte', field: '#26344A', title: OCHRE, ink: CREAM, text: CREAM, accent: RED, accentText: CREAM, motif: '#33445E', sun: OCHRE, clashes: [] },
  { name: 'cipresso', field: '#2F4A2E', title: OCHRE, ink: CREAM, text: CREAM, accent: RED, accentText: CREAM, motif: '#3C5B3A', sun: OCHRE, clashes: ['cypress'] },
  { name: 'terracotta', field: '#C8784A', title: CREAM, ink: BROWN, text: BROWN, accent: BROWN, accentText: CREAM, motif: '#D48A5E', sun: CREAM, clashes: ['red', 'terracotta'] },
  { name: 'cioccolato', field: '#5A3D2B', title: OCHRE, ink: CREAM, text: CREAM, accent: RED, accentText: CREAM, motif: '#6B4A35', sun: OCHRE, clashes: ['brown'] },
  { name: 'salmone', field: '#E0A28C', title: BROWN, ink: BROWN, text: BROWN, accent: RED, accentText: CREAM, motif: '#E8B4A1', sun: CREAM, clashes: ['red', 'terracotta'] },
];

/** Where the car is drawn from, as on a poster: angle round the car from its nose, height, lens. */
export interface PosterPose {
  name: string;
  /** Degrees round the car from its nose (positive: its left side). */
  azimuth: number;
  /** Degrees above the ground. */
  elevation: number;
  /** Lens (vertical field of view, degrees) and how much of the frame the car fills. */
  fov: number;
  fill: number;
  /** Front wheels turned (radians, positive: left), and the body's lean into it. */
  steer: number;
  lean: number;
}

const POSES: PosterPose[] = [
  // Coming at you three-quarters, the classic.
  { name: 'threeQuarter', azimuth: 40, elevation: 15, fov: 22, fill: 0.62, steer: 0.18, lean: 0.03 },
  // Head on and low, filling the sheet (Monza '59).
  { name: 'headOn', azimuth: 6, elevation: 6, fov: 26, fill: 0.72, steer: 0.1, lean: 0.02 },
  // Flat side view, all speed.
  { name: 'profile', azimuth: 90, elevation: 7, fov: 18, fill: 0.62, steer: 0.0, lean: 0.0 },
  // Driving away into the picture.
  { name: 'going', azimuth: 145, elevation: 17, fov: 22, fill: 0.62, steer: -0.2, lean: -0.04 },
  // From above, like the programme cover.
  { name: 'above', azimuth: 55, elevation: 48, fov: 24, fill: 0.62, steer: 0.25, lean: 0.03 },
  // Low and looming, a worm's-eye hero shot.
  { name: 'low', azimuth: 28, elevation: 2.5, fov: 30, fill: 0.7, steer: 0.28, lean: 0.05 },
];

export type PosterMotif = 'flag' | 'sun' | 'rays' | 'stripes';
const MOTIFS: PosterMotif[] = ['flag', 'sun', 'rays', 'stripes'];

export interface PosterStyle {
  palette: PosterPalette;
  pose: PosterPose;
  /** Which side the car faces (mirrors the pose): +1 or -1. */
  side: number;
  motif: PosterMotif;
}

const EPOCH = '2026-09-28';

const DAY_MS = 86400000;
const paletteCache = new Map<string, number>();

/**
 * The day's colour scheme: never one that would swallow the day's car, never
 * one used in the last four days. Worked out day by day from the epoch (cached).
 */
function paletteFor(date: string): number {
  const hit = paletteCache.get(date);
  if (hit !== undefined) return hit;
  const t0 = Date.parse(EPOCH + 'T12:00:00Z');
  const t1 = Date.parse(date + 'T12:00:00Z');
  const recent: number[] = [];
  let pick = 0;
  for (let t = Math.min(t0, t1); t <= t1; t += DAY_MS) {
    const d = new Date(t).toISOString().slice(0, 10);
    const cached = paletteCache.get(d);
    if (cached !== undefined) pick = cached;
    else {
      const main = ERAS[eraForDate(d)].look.main;
      const fits = PALETTES.map((_, i) => i).filter((i) => !PALETTES[i].clashes.includes(main));
      const fresh = fits.filter((i) => !recent.includes(i));
      const pool = fresh.length ? fresh : fits;
      pick = pool[Rng.fromString('poster-palette|' + d).int(0, pool.length - 1)];
      paletteCache.set(d, pick);
    }
    recent.push(pick);
    if (recent.length > 4) recent.shift();
  }
  return pick;
}

export function posterStyleFor(date: string, era: EraId): PosterStyle {
  let pi = paletteFor(date);
  // A preview car that would vanish into the day's field gets the next scheme that suits it.
  const main = ERAS[era].look.main;
  for (let k = 0; k < PALETTES.length && PALETTES[pi].clashes.includes(main); k++) pi = (pi + 1) % PALETTES.length;
  const pose = POSES[spacedCycleForDate(POSES.map((_, i) => i), 'poster-pose', date, 3, EPOCH)];
  const motif = MOTIFS[spacedCycleForDate(MOTIFS.map((_, i) => i), 'poster-motif', date, 2, EPOCH)];
  const day = Math.floor(Date.parse(date + 'T12:00:00Z') / DAY_MS);
  return { palette: PALETTES[pi], pose, side: day % 2 === 0 ? 1 : -1, motif };
}
